import { useEffect, useRef, useState, useCallback } from 'react';
import { io, Socket } from 'socket.io-client';
import { useConnectionState, useProgressState, useChatState } from '../store/useAppStore';
import { wsLogger } from '../utils/socketLogger';
import { debugLogger } from '../utils/debugLogger';
import { Chat, GroupProgress } from '../store/types';

interface SocketConnectionConfig {
  autoConnect?: boolean;
  reconnectionAttempts?: number;
  reconnectionDelay?: number;
  timeout?: number;
}

interface SocketConnectionState {
  isConnected: boolean;
  isConnecting: boolean;
  connectionError: string | null;
  reconnectAttempt: number;
  lastConnectedAt: Date | null;
  latency: number;
}

const SOCKET_SERVER_URL = (import.meta as any).env.VITE_BACKEND_URL;
const DEFAULT_CONFIG: Required<SocketConnectionConfig> = {
  autoConnect: true,
  reconnectionAttempts: 1, // Reduzido de 5 para 1 - tentativa única
  reconnectionDelay: 2000,
  timeout: 30000 // Aumentado de 20s para 30s
};

export const useSocketConnection = (config: SocketConnectionConfig = {}) => {
  const finalConfig = { ...DEFAULT_CONFIG, ...config };
  
  // Zustand stores
  const { authId, setConnectionStatus, setQRCode, setLoading } = useConnectionState();
  const { setGroupProgress } = useProgressState();
  const { setAllChats } = useChatState();
  
  // Local state
  const [socketState, setSocketState] = useState<SocketConnectionState>({
    isConnected: false,
    isConnecting: false,
    connectionError: null,
    reconnectAttempt: 0,
    lastConnectedAt: null,
    latency: 0
  });

  // Refs
  const socketRef = useRef<Socket | null>(null);
  const connectionStartTime = useRef<number>(0);
  const pingStartTime = useRef<number>(0);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const latencyIntervalRef = useRef<ReturnType<typeof setInterval>>();
  const lastConnectedTimeRef = useRef<Date | null>(null);
  const isConnectingRef = useRef<boolean>(false);
  
  // Update socket state helper
  const updateSocketState = useCallback((updates: Partial<SocketConnectionState>) => {
    setSocketState(prev => ({ ...prev, ...updates }));
  }, []);

  // Medição de latência via ping/pong
  const startLatencyMeasurement = useCallback(() => {
    if (!socketRef.current?.connected) return;

    latencyIntervalRef.current = setInterval(() => {
      if (socketRef.current?.connected) {
        pingStartTime.current = Date.now();
        socketRef.current.emit('ping', { timestamp: pingStartTime.current });
        wsLogger.eventEmitted('ping', { timestamp: pingStartTime.current }, authId);
      }
    }, 30000); // A cada 30 segundos
  }, [authId]);

  const stopLatencyMeasurement = useCallback(() => {
    if (latencyIntervalRef.current) {
      clearInterval(latencyIntervalRef.current);
      latencyIntervalRef.current = undefined;
    }
  }, []);

  // Configurar event listeners do Socket.IO
  const setupSocketListeners = useCallback((socket: Socket) => {
    // Conexão estabelecida
    socket.on('connect', () => {
      const connectionTime = Date.now() - connectionStartTime.current;
      const now = new Date();
      
      console.log('🔌 [WebSocket] Connected to server:', { 
        socketId: socket.id, 
        authId, 
        connectionTime: connectionTime + 'ms',
        url: SOCKET_SERVER_URL 
      });
      
      // Store connection time in ref to avoid dependency issues
      lastConnectedTimeRef.current = now;
      
      wsLogger.connected(socket.id || '', authId || '', connectionTime);
      wsLogger.performance('connection_established', connectionTime, authId);
      
      updateSocketState({
        isConnected: true,
        isConnecting: false,
        connectionError: null,
        reconnectAttempt: 0,
        lastConnectedAt: now
      });
      
      // Reset connecting flag
      isConnectingRef.current = false;
      
      setConnectionStatus(true);
      startLatencyMeasurement();
      
      // Join room do authId se disponível
      if (authId) {
        console.log('🔌 [WebSocket] Joining room:', authId);
        socket.emit('join_room', authId);
        wsLogger.eventEmitted('join_room', { room: authId }, authId);
      }
    });

    // Desconexão
    socket.on('disconnect', (reason) => {
      // Use ref to get connection time to avoid dependency issues
      const duration = lastConnectedTimeRef.current 
        ? Date.now() - lastConnectedTimeRef.current.getTime() 
        : 0;
        
      wsLogger.disconnected(reason, authId, duration);
      
      updateSocketState({
        isConnected: false,
        isConnecting: false,
        connectionError: reason
      });
      
      setConnectionStatus(false);
      setLoading(false);
      stopLatencyMeasurement();
      
      // Clear the connection time ref
      lastConnectedTimeRef.current = null;
    });

    // Erro de conexão
    socket.on('connect_error', (error) => {
      console.log('🔌 [WebSocket] Connection error:', error.message);
      wsLogger.error(error, 'connection', authId);
      
      // Reset connecting flag
      isConnectingRef.current = false;
      
      updateSocketState({
        isConnected: false,
        isConnecting: false,
        connectionError: error.message
      });
    });

    // Eventos específicos da aplicação com logs
    
    // QR Code recebido
    socket.on('qr', (data: { to: string; qr: string }) => {
      console.log('🔌 [WebSocket] QR Code received:', { authId, dataLength: data.qr.length });
      wsLogger.eventReceived('qr', data, authId);
      setQRCode(data.qr);
      wsLogger.performance('qr_processing', 0, authId, { qrSize: data.qr.length });
    });

    // Lista de chats recebida
    socket.on('chats', (chats: Chat[]) => {
      wsLogger.eventReceived('chats', { chats }, authId);
      wsLogger.performance('chats_processing', 0, authId, { 
        chatCount: chats.length,
        dataSize: JSON.stringify(chats).length 
      });
      
      // Atualizar chats no store
      setAllChats(chats);
      
      // Parar loading quando chats são recebidos
      setLoading(false);
    });

    // Progresso de envio em grupo
    socket.on('group-progress', (progress: GroupProgress) => {
      wsLogger.eventReceived('group-progress', progress, authId);
      setGroupProgress(progress);
      wsLogger.performance('progress_processing', 0, authId, { 
        percentage: progress.percentage,
        current: progress.current,
        total: progress.total 
      });
    });

    // Progresso de envio em massa
    socket.on('bulk-progress', (data: { attempted: number; total: number; percentage: number }) => {
      wsLogger.eventReceived('bulk-progress', data, authId);
      wsLogger.performance('bulk_progress_processing', 0, authId, data);
    });

    // Falha em envio em massa
    socket.on('bulk-failed', (fail: { number: string; error: string }) => {
      wsLogger.eventReceived('bulk-failed', fail, authId);
    });

    // Status de envio em massa
    socket.on('bulk-status', (data: { message: string }) => {
      wsLogger.eventReceived('bulk-status', data, authId);
    });

    // Status de conexão WhatsApp atualizado
    socket.on('connection-status', (data: { status: string; authId: string; hasQrCode?: boolean; timestamp: string }) => {
      console.log('🔌 [WebSocket] Connection status received:', data);
      wsLogger.eventReceived('connection-status', data, authId);
      
      if (data.status === 'connected') {
        console.log('✅ [WebSocket] WhatsApp connected - updating status');
        setConnectionStatus(true);
        setLoading(false);
        
        // Clear any QR code if present
        if (data.hasQrCode === false) {
          setQRCode(null);
        }
        
        wsLogger.performance('whatsapp_connection_confirmed', 0, authId);
      } else if (data.status === 'disconnected') {
        console.log('❌ [WebSocket] WhatsApp disconnected');
        setConnectionStatus(false);
      }
    });

    // Resposta de ping para medição de latência
    socket.on('pong', (data: { timestamp: number; receivedAt?: number }) => {
      if (pingStartTime.current) {
        const latency = Date.now() - pingStartTime.current;
        wsLogger.latency(latency, authId);
        updateSocketState({ latency });
        pingStartTime.current = 0;
      }
      wsLogger.eventReceived('pong', data, authId);
    });

    // Timeout
    socket.on('connect_timeout', () => {
      wsLogger.timeout(finalConfig.timeout, authId);
      updateSocketState({
        isConnecting: false,
        connectionError: 'Connection timeout'
      });
    });

  }, [authId, finalConfig.timeout, setConnectionStatus, setQRCode, setLoading, setGroupProgress, setAllChats,
      updateSocketState, startLatencyMeasurement, stopLatencyMeasurement]);

  // Conectar ao WebSocket
  const connect = useCallback(() => {
    if (socketRef.current?.connected) {
      console.log('🔌 [WebSocket] Already connected, skipping connection');
      return;
    }

    if (isConnectingRef.current) {
      console.log('🔌 [WebSocket] Connection already in progress, skipping');
      return;
    }

    if (!authId) {
      console.log('🔌 [WebSocket] No authId provided, cannot connect');
      return;
    }

    console.log('🔌 [WebSocket] Attempting to connect:', { 
      url: SOCKET_SERVER_URL, 
      authId: authId.substring(0, 12) + '...',
      config: finalConfig 
    });

    isConnectingRef.current = true;
    connectionStartTime.current = Date.now();
    updateSocketState({ isConnecting: true, connectionError: null });

    wsLogger.stateChanged('disconnected', 'connecting', authId);

    const socket = io(SOCKET_SERVER_URL, {
      query: { authId },
      withCredentials: true,
      transports: ['websocket', 'polling'],
      timeout: finalConfig.timeout,
      reconnection: true,
      reconnectionAttempts: finalConfig.reconnectionAttempts,
      reconnectionDelay: finalConfig.reconnectionDelay,
      forceNew: true
    });

    socketRef.current = socket;
    setupSocketListeners(socket);

  }, [authId, finalConfig, updateSocketState, setupSocketListeners]);

  // Desconectar do WebSocket
  const disconnect = useCallback(() => {
    if (socketRef.current) {
      wsLogger.eventEmitted('disconnect', { reason: 'manual' }, authId);
      socketRef.current.disconnect();
      socketRef.current = null;
    }
    
    // Reset connecting flag
    isConnectingRef.current = false;
    
    stopLatencyMeasurement();
    
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }

    updateSocketState({
      isConnected: false,
      isConnecting: false,
      connectionError: null,
      reconnectAttempt: 0
    });

    setConnectionStatus(false);
    setLoading(false);
  }, [authId, stopLatencyMeasurement, updateSocketState, setConnectionStatus, setLoading]);

  // Reconectar manualmente
  const reconnect = useCallback(() => {
    const attempt = socketState.reconnectAttempt + 1;
    wsLogger.reconnecting(attempt, authId);
    
    updateSocketState({ reconnectAttempt: attempt });
    
    disconnect();
    
    reconnectTimeoutRef.current = setTimeout(() => {
      connect();
    }, finalConfig.reconnectionDelay);
  }, [socketState.reconnectAttempt, authId, disconnect, connect, finalConfig.reconnectionDelay, updateSocketState]);

  // Emitir evento customizado
  const emit = useCallback((eventName: string, data?: any) => {
    if (socketRef.current?.connected) {
      wsLogger.eventEmitted(eventName, data, authId);
      socketRef.current.emit(eventName, data);
      return true;
    } else {
      wsLogger.error(new Error('Socket not connected'), `emit_${eventName}`, authId);
      return false;
    }
  }, [authId]);

  // Auto-connect effect
  useEffect(() => {
    if (finalConfig.autoConnect && authId && !socketRef.current?.connected) {
      console.log('🔌 [WebSocket] Auto-connect triggered', { authId: authId.substring(0, 12) + '...', hasSocket: !!socketRef.current });
      connect();
    }

    return () => {
      // Only disconnect on unmount, not on every re-render
    };
  }, [finalConfig.autoConnect, authId]); // Removed connect and disconnect from dependencies

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      disconnect();
    };
  }, [disconnect]);

  return {
    // Estado da conexão
    ...socketState,
    
    // Métodos de controle
    connect,
    disconnect,
    reconnect,
    emit,
    
    // Informações da conexão
    socket: socketRef.current,
    isSocketReady: socketRef.current?.connected ?? false,
    
    // Estatísticas
    connectionStats: {
      totalConnectTime: socketState.lastConnectedAt 
        ? Date.now() - socketState.lastConnectedAt.getTime() 
        : 0,
      reconnectAttempts: socketState.reconnectAttempt,
      latency: socketState.latency,
      authId: authId?.substring(0, 12) + '...'
    }
  };
};

export default useSocketConnection;